import React, { useEffect, useState } from 'react';
import {
    useGetV1Folder,
    useGetV1FolderFolderIdGetFolderDetails,
    useGetV1FolderHook,
} from '../../api/generated/endpoint';
import { Tree, Loading } from '@appkit4/react-components';

const FileExplorer = ({
    selectedFolderId: initialFolderId = -1,
    onFolderSelect,
}: any) => {
    const [selectedFolderId, setSelectedFolderId] = useState(initialFolderId);
    const [treeData, setTreeData] = useState<any[]>([]);
    const [loadingFolderId, setLoadingFolderId] = useState<number | null>(null);

    const { data: rootFoldersData } = useGetV1Folder({
        parentFolderId: undefined,
    });

    const { data: folderDetails } =
        useGetV1FolderFolderIdGetFolderDetails(initialFolderId);

    const { data: folderData, refetch: refetchFolders } = useGetV1Folder({
        parentFolderId: selectedFolderId === -1 ? undefined : selectedFolderId,
    });

    const getV1Folder = useGetV1FolderHook();

    const removeDuplicateKeyObjects = (treeData: any[]): any[] => {
        const uniqueKeys = new Set<string>();
        const filteredTreeData = [];

        for (const node of treeData) {
            const key = node.key;
            if (!uniqueKeys.has(key)) {
                uniqueKeys.add(key);
                filteredTreeData.push(node);
            }
        }

        return filteredTreeData;
    };

    useEffect(() => {
        async function buildTree(node: any, selectedKey: any, level = 0) {
            if (!node) return null;

            const childrenPromises: any = node.childFolder
                ? [buildTree(node.childFolder, selectedKey, level + 1)]
                : [];

            if (node.folderID) {
                const siblings = await fetchSiblings(node.folderID);
                siblings.forEach((sibling: any) => {
                    childrenPromises.push(
                        buildTree(sibling, selectedKey, level + 1)
                    );
                });
            }

            const children = removeDuplicateKeyObjects(
                await Promise.all(childrenPromises)
            );

            return {
                title: node.folderName,
                key: String(node.folderID || node.folderId),
                expanded: level < 2, // Expand up to a certain level (adjust as needed) or always expanded: true
                iconOpened: 'icon-folder-opened-outline',
                iconClosed: 'icon-folder-closed-outline',
                children: children.filter(Boolean), // Remove null children
                selected: String(node.folderID) === selectedKey,
            };
        }

        async function fetchSiblings(parentFolderId: any) {
            const response = await getV1Folder({ parentFolderId });
            return response.folders || []; // Handle cases where folders might be undefined
        }

        const loadAndUpdateTree = async () => {
            if (initialFolderId === -1) {
                if (rootFoldersData?.folders) {
                    const roots = await Promise.all(
                        rootFoldersData.folders.map(
                            async (child) => await buildTree(child, null) // build tree for each root folder
                        )
                    );
                    setTreeData(roots);
                }
                return;
            }

            if (folderDetails && folderDetails.parentHierarchy) {
                const parentKeys = new Set();
                const collectKeys = (node: any) => {
                    if (node) {
                        parentKeys.add(String(node.folderID)); // Make sure keys are strings
                        node.children?.forEach(collectKeys);
                    }
                };
                collectKeys(folderDetails.parentHierarchy);

                const completeTree = await buildTree(
                    folderDetails.parentHierarchy,
                    String(initialFolderId)
                );

                const rootNodes =
                    rootFoldersData?.folders?.map((folder) => ({
                        title: folder.folderName,
                        key: String(folder.folderId),
                        expanded: false,
                        iconOpened: 'icon-folder-opened-outline',
                        iconClosed: 'icon-folder-closed-outline',
                        children: [],
                    })) || [];

                const resolvedRootNodes = (await Promise.all(rootNodes)).filter(
                    (x) => x.key !== parentKeys.values().next().value
                );

                setTreeData([
                    ...(completeTree ? [completeTree] : []),
                    ...resolvedRootNodes,
                ]);
            }
        };

        loadAndUpdateTree();
    }, [initialFolderId, folderDetails, rootFoldersData]);

    useEffect(() => {
        if (selectedFolderId === -1 || !folderData) return;

        const folders = folderData.folders;

        const newChildren =
            folders.length === 0
                ? [
                      {
                          title: 'No sub folders',
                          key: `no-folders-${selectedFolderId}`,
                          isLeaf: true,
                          disabled: true,
                          noFoldersNode: true,
                          children: [],
                      },
                  ]
                : folders.map((child) => ({
                      title: child.folderName,
                      key: String(child.folderId),
                      expanded: false,
                      iconOpened: 'icon-folder-opened-outline',
                      iconClosed: 'icon-folder-closed-outline',
                      children: [],
                  }));

        function updateTreeWithChildren(nodes: any[]): any[] {
            return nodes.map((node) => {
                if (Number(node.key) === selectedFolderId) {
                    return {
                        ...node,
                        expanded: true,
                        children: newChildren,
                    };
                } else if (node.children?.length > 0) {
                    return {
                        ...node,
                        children: updateTreeWithChildren(node.children),
                    };
                }
                return node;
            });
        }

        setTreeData((prev) => updateTreeWithChildren(prev));
        setLoadingFolderId(null);
    }, [folderData, selectedFolderId]);

    function findNodeByKey(nodes: any[], key: string): any | null {
        for (const node of nodes) {
            if (node.key === key) return node;
            if (node.children) {
                const found = findNodeByKey(node.children, key);
                if (found) return found;
            }
        }
        return null;
    }

    const onClickEvent = async (item: any) => {
        const folderId = Number(item.key);
        onFolderSelect(folderId, item.title);
        const node = findNodeByKey(treeData, item.key);
        const alreadyHasChildren = node?.children?.length > 0;

        if (!alreadyHasChildren) {
            setLoadingFolderId(folderId);
            await refetchFolders({
                queryKey: ['useGetV1Folder', { parentFolderId: folderId }],
            });
            setLoadingFolderId(null);
        }

        setSelectedFolderId(folderId);
    };

    const renderTreeNodeFunc = (item: any, isParentNode: boolean) =>
        isParentNode ? (
            <>
                <span
                    className={`Appkit4-icon ${
                        item.expanded ? item.iconOpened : item.iconClosed
                    }`}
                    aria-hidden={'true'}
                ></span>
                <span className="ap-file-title">{item.title}</span>
            </>
        ) : (
            <>
                <span
                    className={`Appkit4-icon ${
                        item.icon ? item.icon : 'icon-paper-outline'
                    }`}
                    aria-hidden={'true'}
                ></span>
                <span className="ap-file-title">{item.title}</span>
            </>
        );
    return (
        <div className="tree-demo-wrapper">
            {treeData.length === 0 ? (
                <p>Loading...</p>
            ) : (
                <Tree
                    dataTree={treeData}
                    onClick={onClickEvent}
                    onUpdateData={(dataValue) => setTreeData([...dataValue])}
                    renderTreeNode={(node: any, isParentNode) => {
                        const isSelected =
                            String(node.key) === String(selectedFolderId);

                        if (String(node.key) === String(loadingFolderId)) {
                            return (
                                <div key={node.key}>
                                    {renderTreeNodeFunc(node, isParentNode)}
                                    <div style={{ paddingLeft: '1rem' }}>
                                        <Loading
                                            loadingType="circular"
                                            indeterminate
                                            compact={false}
                                        />
                                    </div>
                                </div>
                            );
                        }

                        if (node.noFoldersNode) {
                            return (
                                <div
                                    key={node.key}
                                    style={{
                                        color: 'gray',
                                        cursor: 'default',
                                        userSelect: 'none',
                                        paddingLeft: '1.5rem',
                                    }}
                                    onClick={(e) => e.preventDefault()}
                                >
                                    {renderTreeNodeFunc(node, isParentNode)}
                                </div>
                            );
                        }

                        return (
                            <div key={node.key}>
                                {/* Here, inject bold style for selected */}
                                {isParentNode ? (
                                    <>
                                        <span
                                            className={`Appkit4-icon ${
                                                node.expanded
                                                    ? node.iconOpened
                                                    : node.iconClosed
                                            }`}
                                            aria-hidden={'true'}
                                        ></span>
                                        <span
                                            className="ap-file-title"
                                            style={{
                                                fontWeight: isSelected
                                                    ? 'bold'
                                                    : 'normal',
                                            }}
                                        >
                                            {node.title}
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <span
                                            className={`Appkit4-icon ${
                                                node.icon
                                                    ? node.icon
                                                    : 'icon-paper-outline'
                                            }`}
                                            aria-hidden={'true'}
                                        ></span>
                                        <span
                                            className="ap-file-title"
                                            style={{
                                                fontWeight: isSelected
                                                    ? 'bold'
                                                    : 'normal',
                                            }}
                                        >
                                            {node.title}
                                        </span>
                                    </>
                                )}
                            </div>
                        );
                    }}
                />
            )}
        </div>
    );
};

export default FileExplorer;
